<template>
  <div
    class="flex justify-center rounded-full border border-solid p-0.5"
    :class="[
      { 'border-gray-secondary': hasActiveDot },
      { 'border-white': !hasActiveDot && !fromListingPage },
      // On the listing page only the active swatch shows a ring; inactive
      // swatches keep a transparent border so the row stays aligned.
      { 'border-transparent': !hasActiveDot && fromListingPage },
    ]"
  >
    <span
      :key="color ? `color-picker-color-${color.value}` : 'color-picker-empty'"
      :title="color ? title || color.label : undefined"
      :data-color-id="color?.id"
      :aria-hidden="decorative || undefined"
      :style="
        color
          ? `background:${
              isMultiColor
                ? 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)'
                : displayColor
            }`
          : undefined
      "
      class="flex items-center justify-center rounded-full"
      :class="[
        { 'border border-solid border-black': color && isWhiteColor },
        { 'border border-dotted': color && isNoColor },
        // Unknown/empty colors keep the size but render as an outlined,
        // background-less placeholder instead of collapsing the swatch.
        { 'border border-solid border-gray-secondary': !color },
        {
          'size-[7px] lg:size-[14px]': fromListingPage,
          'size-5 lg:size-[24px]': !fromListingPage,
        },
      ]"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import type { ProductColor } from '@scayle/storefront-nuxt'
import { getColorCodeForId } from '~/utils/product'

const props = defineProps({
  color: {
    type: Object as PropType<ProductColor>,
    default: () => {},
  },
  hasActiveDot: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  fromListingPage: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  title: {
    type: String as PropType<string>,
    default: '',
  },
  // ECOM-2461: when true, hide the decorative swatch from assistive tech so it
  // does not pollute an ancestor link's accessible name. Opt-in (default false)
  // so consumers whose only accessible name is the swatch title stay unaffected.
  decorative: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const displayColor = computed(() => {
  if (!props.color) {
    return '#ffffff'
  }

  if (Number(props.color.value) === props.color.id) {
    const color = getColorCodeForId(props.color.id)
    if (color !== '#ffffff') {
      return color
    }
  }

  const pattern = /^#(?:[0-9a-fA-F]{3}){1,2}$/
  if (pattern.test(`#${props.color.value}`)) {
    return `#${props.color.value}`
  }

  return '#ffffff'
})

const isWhiteColor = computed(
  () => displayColor.value.toUpperCase() === '#FFFFFF',
)
const isNoColor = computed(
  () => getColorCodeForId(props.color.id) === 'no-color',
)
const isMultiColor = computed(
  () => getColorCodeForId(props.color.id) === 'multi-color',
)
</script>
